import { withStyles, Box, PulloverSelect, CURRENCY_LIST_OPTIONS } from 'vopay';
import { Title } from 'UI';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClientData } from '@/providers/ClientData.provider';
import NoOperationalDefault from '../SubAccounts/NoOperationalDefault/NoOperationalDefault';
import { useDialog } from '@/providers/Dialog.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import useFetchAccountBalance from '@/hooks/useApi/useFetchAccountBalance';
import useFetchAccountLimits from '@/hooks/useApi/useFetchAccountLimits';
import TotalBalanceCard from './widgets/InteractiveInfo/TotalBalanceCard';
import TotalPaymentsCard from './widgets/InteractiveInfo/TotalPaymentsCard';
import TotalCollectionsCard from './widgets/InteractiveInfo/TotalCollectionsCard';
import TransactionsCard from './widgets/TransactionsCard/TransactionsCard';
import BankHolidays from './widgets/BankHolidays/BankHolidays';
import RecentTransactions from './widgets/RecentTransactions/RecentTransactions';
import AccountLimits from './widgets/AccountLimits/AccountLimits';
import useDashboardDefaultBankAccount from './useDashboardDefaultBankAccount';
import { useHomepage } from '@/providers/Homepage';

import { format } from 'date-fns';
import styles from './HomepageRevamp.module.scss';

const cn = withStyles(styles);

const Homepage = () => {
  const { t } = useTranslation('translations', { keyPrefix: 'HomepageRevamp' });
  const { userSettings } = useUserSettingsProvider();
  const history = useHistory();
  const fetchDefaultBankAccount = useDashboardDefaultBankAccount();

  const {
    response: { SubAccountType, AccountID, LoginUserName, Success, Country },
  } = useClientData();

  const { state: locationState } = useLocation<{ showPermission?: boolean }>();
  const { hasViewedNoDefaultBankAccountDialog } = useHomepage();
  const currency = Country === 'US' ? 'USD' : 'CAD';
  const { fetching: fetchingAccountBalance, accountBalance, fetchAccountBalance } = useFetchAccountBalance();
  const { fetching: fetchingAccountLimits, accountLimits, fetchAccountLimits } = useFetchAccountLimits();

  const dialog = useDialog();
  const alertDialog = useAlertDialog();
  const currentDate = useMemo(() => format(new Date(), 'MMMM d, yyyy'), []);

  const [activeCurrencyTab, setActiveCurrencyTab] = useState<'CAD' | 'USD'>(currency);
  const currencyOptions = {
    ...CURRENCY_LIST_OPTIONS,
  };

  useEffect(() => {
    if (!hasViewedNoDefaultBankAccountDialog.current && SubAccountType === 'franchise') {
      hasViewedNoDefaultBankAccountDialog.current = true;
      checkDefaultBankAccount();
    }
  }, [AccountID]);

  async function checkDefaultBankAccount() {
    try {
      const defaultBankAccount = await fetchDefaultBankAccount({
        Timezone: userSettings.timezone,
      });

      if (defaultBankAccount) return;

      const confirmation = await dialog.open(NoOperationalDefault);

      if (confirmation) history.push('/operational-accounts');
    } catch (err) {
      console.error(err);
    }
  }

  useEffect(() => {
    if (locationState && locationState.showPermission) {
      alertDialog.caution(t('Dialogue.unauthorizedAccessTitle'), t('Dialogue.unauthorizedAccessSubTitle'), {
        confirmLabel: t('Dialogue.homeButtonLabel'),
      });
    }
  }, [locationState]);

  useEffect(() => {
    if (Success) {
      fetchAccountBalance({
        PortalUser: AccountID,
        Currency: activeCurrencyTab,
      });

      fetchAccountLimits({
        PortalUser: LoginUserName,
        Date: formatUtcDate(new Date()),
      });
    }
  }, [Success, AccountID, LoginUserName, currency, activeCurrencyTab]);

  return (
    <div className={cn('homepageRevamp')}>
      <Title>{t('title')}</Title>
      <Box mt="sm" jc="sb" ai="center">
        <Title level="5" emphasized={false} className={cn('currentDate')}>
          {t('dateLabel')}: {currentDate}
        </Title>
        <Box ai="center" gapSize="8px">
          <Title level="6" emphasized={false} className={cn('selectedCurrency')}>
            {t('selectedCurrency')}
          </Title>
          <PulloverSelect
            name="currency"
            value={activeCurrencyTab}
            onChange={(value) => {
              setActiveCurrencyTab(value);
            }}
          >
            {Object.values(currencyOptions).map((option) => (
              <PulloverSelect.Option key={option} value={option}>
                {option}
              </PulloverSelect.Option>
            ))}
          </PulloverSelect>
        </Box>
      </Box>

      <div className={cn('grid')}>
        <div className={cn('gridItem', 'totalBalance')} role="button" tabIndex={0}>
          <TotalBalanceCard accountBalance={accountBalance} loading={fetchingAccountBalance} />
        </div>

        <div className={cn('gridItem', 'totalPayments')} role="button" tabIndex={0}>
          <TotalPaymentsCard
            loading={fetchingAccountBalance || fetchingAccountLimits}
            currency={accountBalance?.Currency || ''}
            accountLimits={accountLimits}
          />
        </div>

        <div className={cn('gridItem', 'totalCollections')} role="button" tabIndex={0}>
          <TotalCollectionsCard
            loading={fetchingAccountBalance || fetchingAccountLimits}
            currency={accountBalance?.Currency || ''}
            accountLimits={accountLimits}
          />
        </div>

        <div className={cn('card', 'gridItem', 'transactionPerformance')}>
          <TransactionsCard currency={activeCurrencyTab} />
        </div>

        <div className={cn('card', 'gridItem', 'holidays')}>
          <BankHolidays />
        </div>

        <div className={cn('bottomRow')}>
          <div className={cn('card', 'gridItem', 'recentTransactions')}>
            <RecentTransactions currency={activeCurrencyTab} />
          </div>

          <div className={cn('card', 'gridItem', 'accountLimits')}>
            <AccountLimits
              loading={fetchingAccountBalance || fetchingAccountLimits}
              currency={accountBalance?.Currency || ''}
              accountLimits={accountLimits}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

function formatUtcDate(date: Date): string {
  const utcDate = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  return format(utcDate, 'yyyy-MM-dd');
}

export default Homepage;
